import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiPlayFill, RiArrowDownSLine } from 'react-icons/ri';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../services/apiConfig';
import StoryShare from './StoryShare';
import styles from './LibraryPreview.module.css';

const fmt = (seconds) => {
  const t = Math.max(0, Number(seconds) || 0);
  if (!t) return '';
  const h = Math.floor(t / 3600);
  const m = Math.round((t % 3600) / 60);
  return h > 0 ? (m ? `${h}h ${m}m` : `${h}h`) : `${Math.max(1, m)} min`;
};

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const isDone = (x) => ['ready', 'completed'].includes(String(x?.status || '').toLowerCase());

// The sheet a tap on a finished title opens (founder, 2 Oct 2026): first the poster, the synopsis and Play; pulling up (or "More") reveals the
// episodes, the cast, sharing, the seasons and Continue. Data: GET /api/library/{id}/preview.
const LibraryPreview = ({ file: tile, coverSrc, onClose, onPlay, onPlayBook }) => {
  const auth = useAuth();
  // A grouped series: the tabs pick the season; the synopsis, Play and Continue follow it
  const seasons = tile?.seasons || null;
  const [seasonId, setSeasonId] = useState(tile?.id);
  const file = (seasons && seasons.find((x) => x.id === seasonId)) || tile;
  const newest = seasons ? seasons[seasons.length - 1] : tile;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [more, setMore] = useState(false);
  const dialogRef = useRef(null);
  const touch = useRef(null);
  const isStory = String(file?.documentType || '').toLowerCase() === 'story_time';

  // Continue: the newest finished season of a series can start the next one (3 credits)
  const canContinue = isStory && file?.id === newest?.id && isDone(file);
  const [cont, setCont] = useState('idle'); // idle | confirm | sending | done | error
  const [contMsg, setContMsg] = useState('');
  const requestId = useRef(`continue-${tile?.id}-${Date.now()}`);
  const startNextSeason = async () => {
    setCont('sending');
    try {
      const token = await auth.getToken();
      const res = await fetch(getApiUrl('/api/story-time/jobs'), { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ continueOf: file.id, clientRequestId: requestId.current }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || 'We could not start the next season.');
      setCont('done'); setContMsg('The next season is on its way. It will appear here when it is ready.');
      window.dispatchEvent(new Event('library:refresh'));
    } catch (e) { setCont('error'); setContMsg(e.message || 'We could not start the next season.'); }
  };

  useEffect(() => {
    let alive = true;
    setData(null); setError(false);
    (async () => {
      try {
        const token = await auth.getToken();
        const res = await fetch(getApiUrl(`/api/library/${encodeURIComponent(file.id)}/preview`), { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (alive) setData(json);
      } catch (e) {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, [file?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prev = document.activeElement;
    const node = dialogRef.current;
    node?.querySelector(FOCUSABLE)?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled);
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); if (prev && prev.focus) prev.focus(); };
  }, [onClose]);

  // Pull up to see more, pull down (from the top) to close
  const onTouchStart = (e) => { touch.current = { y: e.touches[0].clientY, top: dialogRef.current?.scrollTop || 0 }; };
  const onTouchEnd = (e) => {
    const t = touch.current; touch.current = null;
    if (!t) return;
    const dy = e.changedTouches[0].clientY - t.y;
    if (dy < -48 && !more) setMore(true);
    else if (dy > 64 && t.top === 0 && (dialogRef.current?.scrollTop || 0) === 0) onClose();
  };

  const title = (data?.title || file?.name || 'Untitled').replace(/:\s*Season \d+$/, '');
  const genreRaw = String(data?.genre || file?.genre || '');
  const genre = genreRaw ? genreRaw[0].toUpperCase() + genreRaw.slice(1) : '';
  const count = data?.chapterCount || 0;
  const total = fmt(data?.totalDuration || file?.totalDuration);
  const unit = isStory ? 'episode' : 'chapter';
  const list = isStory ? (data?.episodes || []) : (data?.chapters || []);
  const play = () => { onClose(); if (file.id !== tile.id && onPlayBook) onPlayBook(file); else onPlay(); };
  const nextSeason = (file.season || 1) + 1;

  return createPortal(
    <div className={styles.overlay} onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div ref={dialogRef} className={`${styles.dialog} ${more ? styles.dialogMore : ''}`} role="dialog" aria-modal="true" aria-labelledby="library-preview-title"
           onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className={styles.grab} aria-hidden="true" />
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close"><RiCloseLine size={20} /></button>

        <div className={styles.hero}>
          {coverSrc ? <img className={styles.cover} src={coverSrc} alt="" /> : <div className={styles.coverBlank} />}
          <div className={styles.heroText}>
            <h2 id="library-preview-title" className={styles.title}>{title}</h2>
            <p className={styles.meta}>
              {[genre, seasons ? `Season ${file.season || 1}` : null, count > 0 ? `${count} ${unit}${count === 1 ? '' : 's'}` : null, total].filter(Boolean).join(' · ')}
            </p>
            <button type="button" className={styles.play} onClick={play}><RiPlayFill size={18} /> Play</button>
          </div>
        </div>

        {data?.logline ? <p className={styles.logline}>{data.logline}</p>
          : data?.excerpt ? <p className={styles.logline}>{data.excerpt}</p>
          : error ? <p className={styles.muted}>Could not load the details right now.</p>
          : <p className={styles.muted}>Loading...</p>}

        {!more ? (
          <button type="button" className={styles.moreBtn} onClick={() => setMore(true)}>
            More <RiArrowDownSLine size={18} />
          </button>
        ) : (
          <div className={styles.more}>
            {list.length > 0 && (
              <section className={styles.section}>
                <div className={styles.listHead}>
                  <h3 className={styles.h3}>{isStory ? 'Episodes' : 'Chapters'}</h3>
                  {seasons && (
                    <div className={styles.seg} role="tablist" aria-label="Season">
                      {seasons.map((x) => {
                        const off = !isDone(x);
                        return (
                          <button key={x.id} type="button" role="tab" aria-selected={x.id === file.id} disabled={off}
                            title={off ? (String(x.status || '').toLowerCase() === 'failed' ? 'This season failed' : 'Still being made') : undefined}
                            className={`${styles.segBtn} ${x.id === file.id ? styles.segOn : ''}`} onClick={() => { setSeasonId(x.id); setCont('idle'); }}>
                            S{x.season || 1}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <ol className={styles.list}>
                  {list.map((it, i) => (
                    <li key={i} className={styles.item}>
                      <div className={styles.itemHead}>
                        <span className={styles.itemTitle}>{it.title || `${isStory ? 'Episode' : 'Chapter'} ${i + 1}`}</span>
                        {it.duration > 0 && <span className={styles.itemDur}>{fmt(it.duration)}</span>}
                      </div>
                      {it.story && <p className={styles.itemStory}>{it.story}</p>}
                    </li>
                  ))}
                </ol>
              </section>
            )}
            {data?.cast?.length > 0 && (
              <section className={styles.section}>
                <h3 className={styles.h3}>Cast</h3>
                <ul className={styles.cast}>
                  {data.cast.map((c) => <li key={c.name}><strong>{c.name}</strong>{c.who ? `: ${c.who}` : ''}</li>)}
                </ul>
              </section>
            )}
            {canContinue && (
              <section className={styles.section}>
                {cont === 'idle' && (
                  <button type="button" className={styles.lineBtn} onClick={() => setCont('confirm')}>
                    <span>Continue the story</span><span className={styles.note}>Season {nextSeason} · 3 credits</span>
                  </button>
                )}
                {cont === 'confirm' && (
                  <div>
                    <p className={styles.muted}>Same cast and world, picking up where this season ends.</p>
                    <div className={styles.row}>
                      <button type="button" className={styles.play} onClick={startNextSeason}>Start season {nextSeason} · 3 credits</button>
                      <button type="button" className={styles.textBtn} onClick={() => setCont('idle')}>Not now</button>
                    </div>
                  </div>
                )}
                {cont === 'sending' && <p className={styles.muted}>Starting the next season...</p>}
                {(cont === 'done' || cont === 'error') && <p className={cont === 'error' ? styles.err : styles.muted}>{contMsg}</p>}
              </section>
            )}
            {isStory && isDone(file) && (
              <section className={styles.section}>
                <h3 className={styles.h3}>Share</h3>
                <StoryShare jobId={file.id} />
              </section>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LibraryPreview;
